import React, { useCallback, useMemo } from "react";

import Big from "big.js";
import { v4 as uuidv4 } from "uuid";

import { useSelector } from "react-redux";

import { Button, SecondaryButton } from "components/Form";
import { Column } from "components/Layout";
import {
  DialogActions,
  DialogContent,
  DialogTitle,
  ZoomyDialog,
} from "components/MaterialDialog";

import { SmallText } from "components/Text";
import { useSubmitHandler } from "components/Form/FormikControls";

import { Tooltip } from "components/Form/FormikControls/Tooltip";
import {
  getIsLivestockAgent,
  getIsSaleyardAdmin,
  getSideLoadedBusinessById,
  getSideLoadedBusinessMappableFieldsById,
  selectBusinessIdByAgentCodeLookup,
  selectBusinessIdBySaleyardCodeLookup,
} from "selectors";
import { Form, Formik } from "formik";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
} from "@material-ui/core";

import * as Yup from "yup";
import {
  fieldConfigurations,
  getSideLoadedFieldConfigurationsForUser,
  NOTES_FIELD_INDICES,
  FieldConfiguration,
} from "./fieldConfigurations";
import {
  LIVESTOCK_AGENT_CONTENT_TYPE_NAME,
  SALEYARD_ADMIN_CONTENT_TYPE_NAME,
} from "constants/users";
import { SideLoadedFieldRow } from "./SideLoadedFieldRow";
import { VendorCommissionBandTypes } from "constants/vendorCommissionBands";
import {
  businessRelations,
  businessRelationsRateTypes,
} from "constants/businesses";
import { buildTransitInsuranceFieldsFromSideLoadedBusinessValues } from "lib/insuranceRates";

interface SideLoadedFieldCompareBodyProps {
  onClose: () => void;
  onBack?: () => void;
  recordId: string;
  businessId?: string;
  configurations: FieldConfiguration[];
}

const SideLoadedFieldCompareBody = ({
  onClose,
  onBack,
  recordId,
  businessId,
  configurations,
}: SideLoadedFieldCompareBodyProps) => {
  const [isSubmitEnabled, setIsSubmitEnabled] = React.useState(false);
  useSubmitHandler(true, setIsSubmitEnabled);

  const sideLoadedBusiness = useSelector(getSideLoadedBusinessById(recordId));

  return (
    <>
      <DialogTitle onClose={onClose}>
        Data Suggestions for &quot;{sideLoadedBusiness.name}&quot;
      </DialogTitle>
      <DialogContent dividers>
        <Column full>
          <SmallText>
            Choose the data you&apos;d like to use from the &quot;
            {sideLoadedBusiness.name}&quot; record in &quot;
            {sideLoadedBusiness.datasetName}&quot; .
          </SmallText>

          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>
                  AgriNous Field
                  <Tooltip title="Where in AgriNous you will be mapping data to." />
                  <br />
                  <i>Current Data</i>
                </TableCell>
                <TableCell>
                  Source Field{" "}
                  <Tooltip title="The field in your data source to match to the AgriNous Field." />
                  <br />
                  <i>New Data</i>
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {configurations.map(configuration => (
                <SideLoadedFieldRow
                  key={configuration.value}
                  businessId={businessId}
                  configuration={configuration}
                  recordId={recordId}
                />
              ))}
            </TableBody>
          </Table>
        </Column>
      </DialogContent>
      <DialogActions>
        <SecondaryButton type="button" onClick={onClose}>
          Cancel
        </SecondaryButton>
        {onBack && (
          <SecondaryButton type="button" onClick={onBack}>
            Back
          </SecondaryButton>
        )}
        <Button type="submit" disabled={!isSubmitEnabled}>
          Use Data
        </Button>
      </DialogActions>
    </>
  );
};

interface SideLoadedContact {
  id: number;
  index: number;
  isComplianceReportRecipient: boolean;
  isCommercialReportRecipient: boolean;
  email?: string;
  firstName?: string;
  lastName?: string;
  phone?: string;
}

interface SideLoadedProperty {
  id: string;
  PIC: string;
  name?: string;
  isShown: boolean;
}

export interface SideLoadedSelectValues {
  recordId: string;
  fields: Record<string, string>;
  contacts: SideLoadedContact[];
  properties: SideLoadedProperty[];
  vendorCommissions: VendorCommissionBand[];
  relationships: BusinessRelation[];
  notes: string | null;
}
interface SideLoadedFieldCompareModalProps {
  onClose: () => void;
  onSelect: (selectedValues: SideLoadedSelectValues) => void;
  businessId?: string;
  recordId: string;
  onBack?: () => void;
}

function validateRebateAgentRequiredFactory(mappableFieldValue) {
  return function () {
    // `this` is the Yup test context; access sibling fields via this.parent
    const rebateAgentMappableFieldValue =
      this.parent[`MappableField__${fieldConfigurations.REBATE_AGENT.value}`];
    if (mappableFieldValue && !rebateAgentMappableFieldValue) {
      return false;
    }
    // otherwise valid
    return true;
  };
}

interface SideLoadedFieldCompareModalValues {
  [key: string]: string;
}

const SideLoadedFieldCompareModal = ({
  onClose,
  onSelect,
  businessId,
  recordId,
  onBack,
}: SideLoadedFieldCompareModalProps): React.JSX.Element => {
  const isSaleyardAdmin = useSelector(getIsSaleyardAdmin);
  const isLivestockAgent = useSelector(getIsLivestockAgent);
  let userRole: string | null = null;
  if (isSaleyardAdmin) {
    userRole = SALEYARD_ADMIN_CONTENT_TYPE_NAME;
  } else if (isLivestockAgent) {
    userRole = LIVESTOCK_AGENT_CONTENT_TYPE_NAME;
  }
  const visibleFieldConfigurations = useMemo(
    () => getSideLoadedFieldConfigurationsForUser(userRole),
    [userRole],
  );

  const businessIdByAgentCodeLookup = useSelector(
    selectBusinessIdByAgentCodeLookup as (
      state: State,
    ) => Record<string, string>,
  );
  const businessIdBySaleyardCodeLookup = useSelector(
    selectBusinessIdBySaleyardCodeLookup as (
      state: State,
    ) => Record<string, string>,
  );

  const handleSubmit = useCallback(
    (values: SideLoadedFieldCompareModalValues) => {
      // Inject a negative base, incremented by the index in order to ensure uniqueness, for contacts.
      const idBase = Date.now() * -1;
      const populatedValues: SideLoadedSelectValues = Object.entries(
        values,
      ).reduce(
        (acc: SideLoadedSelectValues, [key, targetField], index: number) => {
          if (key.startsWith("MappableField__") && targetField !== null) {
            // The field in AgriNous.
            const mappableField = key.replace("MappableField__", "");

            // Find the configuration for this mappable field.
            const configuration = Object.values(fieldConfigurations).find(
              config => config.value === mappableField,
            );

            // The value of what they've selected (or manually fiddled with)
            const newValue = values[`Value__${mappableField}`];

            if (configuration && configuration.handleAsProperty && newValue) {
              // Simple enough - we're just ingesting PICs, not names, etc.
              acc.properties.push({
                id: uuidv4(),
                PIC: newValue,
                name: "",
                isShown: true,
              });
            } else if (
              configuration &&
              configuration.handleAsContact &&
              newValue
            ) {
              // A bit more complex.... email1, firstname1, lastname1, phone1, etc need to be tied together.
              // See if we have a contact with this index already.
              const existingContact = acc.contacts.find(
                contact => contact.index === configuration.index,
              );
              if (existingContact) {
                // If we do - update the existing contact.
                existingContact[configuration.fieldName] = newValue;
              } else {
                // If we don't - add a new contact, with just this field, and general defaults.
                acc.contacts.push({
                  id: idBase - index,
                  index: configuration.index,
                  isComplianceReportRecipient: true,
                  isCommercialReportRecipient: true,
                  [configuration.fieldName]: newValue,
                });
              }
            } else if (
              configuration &&
              configuration.value ===
                fieldConfigurations.TRANSIT_INSURANCE.value
            ) {
              Object.assign(
                acc.fields,
                buildTransitInsuranceFieldsFromSideLoadedBusinessValues(
                  newValue,
                ),
              );
            } else if (
              configuration &&
              configuration.value ===
                fieldConfigurations.VENDOR_PERCENTAGE_COMMISSION.value
            ) {
              // Inject a row for 'all types' in the commissions table.
              acc.vendorCommissions.push({
                id: uuidv4(),
                commissionType: VendorCommissionBandTypes.Percentage,
                // Note - we're dividing by 100 here to get the percentage.

                commissionValue: Number(newValue) / 100,
                saleSubTypeId: null,
              });
            } else if (
              configuration &&
              [
                fieldConfigurations.STOCK_AGENT.value,
                fieldConfigurations.REBATE_AGENT.value,
              ].includes(configuration.value)
            ) {
              // Inject a row for the relationship, and it's rate value.
              const relatedToId =
                businessIdByAgentCodeLookup[newValue] ||
                businessIdBySaleyardCodeLookup[newValue];

              // Pull the stock agent percentage, if this is a stock agent field; rebate for rebate agent.
              const rateValueFieldName =
                configuration.value === fieldConfigurations.STOCK_AGENT.value
                  ? fieldConfigurations.STOCK_AGENT_PERCENTAGE_COMMISSION.value
                  : fieldConfigurations.REBATE_AGENT_PERCENTAGE_COMMISSION
                      .value;

              // Mark the relationship type as stock or rebate.
              const relationType =
                configuration.value === fieldConfigurations.STOCK_AGENT.value
                  ? businessRelations.STOCK_AGENT
                  : businessRelations.REBATE_AGENT;

              // Pull the rate value as a percentage, retaining the original decimal places, without float precision errors.
              const rateValue: string = new Big(
                values[`Value__${rateValueFieldName}`] || 0,
              )
                .div(100)
                .toString();

              acc.relationships.push({
                id: uuidv4(),
                relatedFromId: businessId,
                relatedToId,
                relationType,
                rateValue,
                rateType: businessRelationsRateTypes.PERCENTAGE,
                saleType: null,
                saleSubTypeId: null,
                speciesId: null,
              });
            } else if (configuration && configuration.handleAsNotes) {
              acc.notes = newValue;
            } else if (
              newValue &&
              configuration.value !==
                fieldConfigurations.STOCK_AGENT_PERCENTAGE_COMMISSION.value &&
              configuration.value !==
                fieldConfigurations.REBATE_AGENT_PERCENTAGE_COMMISSION.value
            ) {
              // Fallback to a dumb-set, except for the commission fields, which are handled above.
              acc.fields[configuration.targetFieldName] = newValue;
            }
          }
          return acc;
        },
        {
          recordId,
          fields: {},
          contacts: [],
          properties: [],
          vendorCommissions: [],
          relationships: [],
          notes: null,
        },
      );
      onSelect(populatedValues);
    },
    [
      onSelect,
      recordId,
      businessIdByAgentCodeLookup,
      businessIdBySaleyardCodeLookup,
      businessId,
    ],
  );

  const sideLoadedBusiness = useSelector(getSideLoadedBusinessById(recordId));

  const mappableFields = useSelector(
    getSideLoadedBusinessMappableFieldsById(recordId),
  );

  const initialValues = useMemo(
    () =>
      visibleFieldConfigurations.reduce((acc, configuration) => {
        acc[`MappableField__${configuration.value}`] = null;
        acc[`Value__${configuration.value}`] = "";

        mappableFields.forEach(sourceFieldName => {
          const simplifiedSourceName = sourceFieldName
            .toLowerCase()
            .replace(/[^a-z0-9]/g, "");
          if (
            configuration.sourceFieldSuggestions.includes(
              simplifiedSourceName,
            ) &&
            sideLoadedBusiness.rawData[sourceFieldName]
          ) {
            if (configuration.handleAsNotes) {
              acc[`MappableField__${configuration.value}`] = true;
              const firstEmptyIndex = NOTES_FIELD_INDICES.find(
                index =>
                  !acc[`MappableField__${configuration.value}__${index}`],
              );
              if (firstEmptyIndex !== undefined) {
                acc[
                  `MappableField__${configuration.value}__${firstEmptyIndex}`
                ] = sourceFieldName;
              }
              acc[`Value__${configuration.value}`] = ""; // Notes are handled by the NotesValueEditor component.
            } else {
              acc[`MappableField__${configuration.value}`] = sourceFieldName;
              acc[`Value__${configuration.value}`] =
                sideLoadedBusiness.rawData[sourceFieldName];
            }
          }
        });

        return acc;
      }, {}),
    [mappableFields, sideLoadedBusiness, visibleFieldConfigurations],
  );

  const validationSchema = useMemo(() => {
    const shape: Record<string, Yup.AnySchema> = {};
    visibleFieldConfigurations.forEach(configuration => {
      shape[`MappableField__${configuration.value}`] = Yup.string().nullable();
      shape[`Value__${configuration.value}`] = Yup.string().when(
        `MappableField__${configuration.value}`,
        (mappableFieldValue, schema) => {
          if (configuration.isBusinessLookup) {
            return Yup.string().test({
              test: () => {
                const selectedValue =
                  sideLoadedBusiness.rawData[mappableFieldValue];
                if (!selectedValue) {
                  return true;
                }
                const selectedBusinessId =
                  businessIdByAgentCodeLookup[selectedValue] ||
                  businessIdBySaleyardCodeLookup[selectedValue] ||
                  null;
                return Boolean(selectedBusinessId);
              },
            });
          } else if (
            configuration.value ===
            fieldConfigurations.REBATE_AGENT_PERCENTAGE_COMMISSION.value
          ) {
            return Yup.string().test({
              name: "rebateAgentRequired",
              test: validateRebateAgentRequiredFactory(mappableFieldValue),
              message:
                "Rebate Agent is required when Rebate Agent Percentage Commission is present.",
            });
          }

          if (mappableFieldValue !== null) {
            return configuration.validation || Yup.string();
          }
          return schema;
        },
      );
    });
    return Yup.object().shape(shape);
  }, [
    sideLoadedBusiness,
    businessIdByAgentCodeLookup,
    businessIdBySaleyardCodeLookup,
    visibleFieldConfigurations,
  ]);

  return (
    <ZoomyDialog open onClose={onClose} maxWidth="lg" fullWidth>
      <Formik
        onSubmit={handleSubmit}
        initialValues={initialValues}
        validationSchema={validationSchema}
        validateOnMount
      >
        <Form>
          <SideLoadedFieldCompareBody
            configurations={visibleFieldConfigurations}
            businessId={businessId}
            recordId={recordId}
            onClose={onClose}
            onBack={onBack}
          />
        </Form>
      </Formik>
    </ZoomyDialog>
  );
};

export default SideLoadedFieldCompareModal;
